import React, { useState, useEffect } from 'react';
import { Pencil, Trash2, X } from 'lucide-react';
import { frontendDb as db } from '../../lib/frontend-db';
import type { NewsItem } from '../../lib/frontend-db';

const NewsPage = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [editingNewsItem, setEditingNewsItem] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createdAt, setCreatedAt] = useState<string>('');
  const [fetchingTime, setFetchingTime] = useState<boolean>(false);
  const [pageTitle, setPageTitle] = useState('Nachrichten');

  useEffect(() => {
    loadNews();
    loadPageTitle();
  }, []);

  const loadPageTitle = async () => {
    try {
      const settings = await db.getSettings();
      if (settings) {
        setPageTitle((settings as any).news_title || 'Aktuelles');
      }
    } catch (error) {
      console.error('Error loading page title:', error);
    }
  };

  const loadNews = async () => {
    try {
      const newsItems = await db.getNews();
      setNews(newsItems);
    } catch (error) {
      console.error('Error loading news:', error);
      setError('Fehler beim Laden der Nachrichten');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let imageUrl = editingNewsItem?.image_url || '';

      if (selectedImage) {
        try {
          console.log('📤 Starting image upload for news item');
          imageUrl = await db.storeImage(selectedImage);
          console.log('✅ Image uploaded successfully:', imageUrl);
        } catch (imageError) {
          console.error('❌ Error uploading image:', imageError);
          setError('Fehler beim Hochladen des Bildes: ' + (imageError as Error).message);
          return;
        }
      }

      // Ermittele Datum: Eingabe -> NTP -> ansonsten DB-Default (created_at nicht senden)
      let createdAtSql: string | undefined = undefined;
      if (createdAt && createdAt.trim() !== '') {
        try {
          createdAtSql = new Date(createdAt).toISOString().slice(0, 19).replace('T', ' ');
          console.log('📅 Using provided date:', createdAtSql);
        } catch (dateError) {
          console.error('❌ Error parsing date:', dateError);
          setError('Ungültiges Datum. Bitte verwenden Sie das Format YYYY-MM-DD HH:MM:SS');
          return;
        }
      } else {
        try {
          const { berlinIso } = await db.getServerTime();
          createdAtSql = berlinIso.replace('T', ' ');
          console.log('📅 Using server time:', createdAtSql);
        } catch (timeError) {
          console.error('❌ Error getting server time:', timeError);
          createdAtSql = undefined; // DB CURRENT_TIMESTAMP verwenden
        }
      }

      if (editingNewsItem) {
        try {
          const payload: any = {
            ...editingNewsItem,
            title,
            content,
            image_url: imageUrl,
          };
          if (createdAtSql) payload.created_at = createdAtSql;
          await db.updateNews(editingNewsItem.id, payload);
          console.log('✅ News item updated successfully');
        } catch (updateError) {
          console.error('❌ Error updating news item:', updateError);
          setError('Fehler beim Aktualisieren der Nachricht: ' + (updateError as Error).message);
          return;
        }
      } else {
        try {
          const payload: any = { title, content, image_url: imageUrl };
          if (createdAtSql) payload.created_at = createdAtSql;
          await db.createNews(payload);
          console.log('✅ News item created successfully');
        } catch (createError) {
          console.error('❌ Error creating news item:', createError);
          setError('Fehler beim Erstellen der Nachricht: ' + (createError as Error).message);
          return;
        }
      }

      resetForm();
      // Seite hart neu laden, damit Admin-Panel konsistent ist
      window.location.reload();
    } catch (error) {
      console.error('❌ Unexpected error saving news:', error);
      setError('Unerwarteter Fehler beim Speichern der Nachricht: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const setTodayFromServer = async () => {
    try {
      setFetchingTime(true);
      const { berlinDate } = await db.getServerTime();
      const [day, month, year] = berlinDate.includes('.')
        ? [berlinDate.substring(0,2), berlinDate.substring(3,5), berlinDate.substring(6,10)]
        : [berlinDate.split('-')[2], berlinDate.split('-')[1], berlinDate.split('-')[0]];
      setCreatedAt(`${year}-${month}-${day}`);
    } catch (error) {
      console.error('NTP Datum laden fehlgeschlagen:', error);
    } finally {
      setFetchingTime(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Möchten Sie diese Nachricht wirklich löschen?')) {
      return;
    }

    try {
      await db.deleteNews(id);
      window.location.reload();
    } catch (error) {
      console.error('Error deleting news:', error);
      setError('Fehler beim Löschen der Nachricht');
    }
  };

  const resetForm = () => {
    setEditingNewsItem(null);
    setTitle('');
    setContent('');
    setSelectedImage(null);
    setPreviewUrl('');
    setCreatedAt('');
  };

  if (loading && news.length === 0) {
    return <div className="animate-pulse">Lädt...</div>;
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4 text-[var(--primary-color)]">
          {editingNewsItem ? 'Nachricht bearbeiten' : 'Neue Nachricht erstellen'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Titel</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Inhalt</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Bild</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="mt-1 block w-full"
              required={!editingNewsItem?.image_url}
            />
            {(previewUrl || editingNewsItem?.image_url) && (
              <div className="mt-2">
                <img
                  src={previewUrl || editingNewsItem?.image_url}
                  alt="Vorschau"
                  className="w-full max-h-48 object-cover rounded"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Datum</label>
            <input
              type="date"
              value={createdAt}
              onChange={(e) => setCreatedAt(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-gray-500">Leer lassen, um das aktuelle Datum zu verwenden.</p>
              <button type="button" onClick={setTodayFromServer} className="text-xs text-green-700 hover:text-green-800">
                {fetchingTime ? 'Lade…' : 'Heute (NTP) setzen'}
              </button>
            </div>
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-green-700 text-white py-2 px-4 rounded-md hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {loading ? 'Wird gespeichert...' : (editingNewsItem ? 'Aktualisieren' : 'Erstellen')}
            </button>
            {editingNewsItem && (
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300"
              >
                Abbrechen
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4 text-[var(--primary-color)]">{pageTitle}</h2>
        <div className="space-y-6">
          {news.map((item) => (
            <div key={item.id} className="border-b pb-6">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-semibold text-lg">{item.title}</h3>
                  <p className="text-sm text-gray-600">
                    {new Date(item.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setEditingNewsItem(item);
                      setTitle(item.title);
                      setContent(item.content);
                      setPreviewUrl(item.image_url);
                      setCreatedAt(item.created_at ? item.created_at.substring(0, 10) : '');
                    }}
                    className="p-1 text-green-700 hover:text-green-800"
                  >
                    <Pencil size={20} />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-1 text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
              <p className="text-gray-700 mb-2">{item.content}</p>
              {item.image_url && (
                <img
                  src={item.image_url}
                  alt={item.title}
                  className="w-full max-h-48 object-cover rounded"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewsPage;

